import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Modal } from '@/components/modal';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DialogClose } from '@/components/ui/dialog';
import { LoadingSpinner } from '@/components/loading-spinner';
import { CopyPlus } from 'lucide-react';

interface SaveRunAsNewWorkflowInputModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (name: string) => Promise<void>;
  isLoading?: boolean;
}

export const SaveRunAsNewWorkflowInputModal: React.FC<
  SaveRunAsNewWorkflowInputModalProps
> = ({ isOpen, onClose, onConfirm, isLoading = false }) => {
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState('');

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setName('Untitled Workflow');
      setNameError('');
    }
  }, [isOpen]);

  const handleConfirm = async () => {
    const trimmedName = name.trim();

    if (!trimmedName) {
      setNameError('Workflow name is required');
      return;
    }

    // 255 matches the backend workflow.name VARCHAR(255) column.
    if (trimmedName.length > 255) {
      setNameError('Workflow name cannot exceed 255 characters');
      return;
    }

    setNameError('');
    await onConfirm(trimmedName);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && name.trim()) {
      e.preventDefault();
      handleConfirm();
    }
  };

  const Footer = () => (
    <>
      <DialogClose asChild>
        <Button onClick={onClose} variant='outline' disabled={isLoading}>
          Cancel
        </Button>
      </DialogClose>
      <Button onClick={handleConfirm} disabled={isLoading || !name.trim()}>
        {isLoading ? (
          <>
            <LoadingSpinner />
            Creating...
          </>
        ) : (
          <>
            <CopyPlus className='size-4 mr-2' />
            Create Workflow
          </>
        )}
      </Button>
    </>
  );

  return (
    <Modal
      isDialogOpen={isOpen}
      setIsDialogOpen={(open) => {
        if (!open && !isLoading) {
          onClose();
        }
      }}
      title='Save Run as New Workflow'
      description='Create a new workflow from this past run'
      footer={<Footer />}
    >
      <div className='space-y-4'>
        <div className='space-y-2'>
          <Label htmlFor='workflow-name'>
            Workflow Name <span className='text-red-500'>*</span>
          </Label>
          <Input
            id='workflow-name'
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (nameError) setNameError('');
            }}
            onKeyDown={handleKeyDown}
            placeholder='Enter workflow name'
            disabled={isLoading}
            className={nameError ? 'border-red-500' : ''}
            autoFocus
          />
          {nameError && <p className='text-sm text-red-500'>{nameError}</p>}
        </div>

        <p className='text-sm text-muted-foreground'>
          The new workflow will include all nodes, edges, and configuration from
          this run.
        </p>
      </div>
    </Modal>
  );
};
